// Browser-side event tracking for the trend simple-checkout embed.
//
// Reuses the gtag + fbq globals loaded by the host page (shop.trend.at) — no
// pixel re-init, no script tags. OneTrust consent gating happens upstream on
// the host page, so calls made here only fire if the user has consented.
//
// Tracking ids are pulled from the shop's installed tags and hardcoded
// here; OpenOptions.tracking overrides everything per integration.

export type TrackingConfig = {
  ga4Id?: string;
  googleAdsId?: string;
  googleAdsConversionLabel?: string;
  metaPixelId?: string;
  // If true, embed will NOT call gtag/fbq itself — useful when the host wants
  // to handle events through its own callbacks only.
  disabled?: boolean;
};

export type PurchaseData = {
  transactionId: string;
  value: number;
  currency: string;
  itemId?: string;
  itemName?: string;
};

export const DEFAULT_TRACKING: Required<Omit<TrackingConfig, 'disabled'>> = {
  ga4Id: 'G-F4YWN0JQWN',
  googleAdsId: 'AW-16625379250',
  googleAdsConversionLabel: 'GU8fCPr5pbUaELLPzPc9',
  metaPixelId: '280182465941903',
};

type Gtag = (...args: unknown[]) => void;
type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    fbq?: Fbq;
    ShopifyAnalytics?: { meta?: { currency?: string } };
  }
}

function gtag(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
    return;
  }

  console.debug('[TrendCheckout] gtag not loaded on host — skipping', args);
}

function fbq(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq === 'function') {
    window.fbq(...args);
    return;
  }
  // ponytail: no stub — Meta's base code bails on an existing window.fbq, so
  // stubbing here would stop the host's pixel from ever loading. Calls made
  // before the pixel loads are dropped; move to Meta's stub snippet (n.queue)
  // only if that turns out to matter.
  console.debug('[TrendCheckout] fbq not loaded on host — skipping', args);
}

// Meta product params — itemId is the Shopify variant id (see detectProduct).
function metaContents(itemId?: string, itemName?: string, value?: number) {
  if (!itemId) return {};
  return {
    content_type: 'product_variant',
    content_ids: [itemId],
    content_name: itemName,
    contents: [{ id: itemId, quantity: 1, item_price: value }],
  };
}

export function trackBeginCheckout(
  cfg: TrackingConfig,
  data: { value?: number; currency?: string; itemId?: string; itemName?: string },
): void {
  if (cfg.disabled) return;
  const value = data.value;
  const currency = data.currency || 'EUR';
  gtag('event', 'begin_checkout', {
    send_to: cfg.ga4Id ?? DEFAULT_TRACKING.ga4Id,
    currency,
    value,
    items: data.itemId ? [{ item_id: data.itemId, item_name: data.itemName }] : undefined,
  });
  fbq('track', 'InitiateCheckout', { value, currency, ...metaContents(data.itemId, data.itemName, value) });
}

export function trackAddPaymentInfo(
  cfg: TrackingConfig,
  data: { value?: number; currency?: string; itemId?: string; itemName?: string },
): void {
  if (cfg.disabled) return;
  const value = data.value;
  const currency = data.currency || 'EUR';
  gtag('event', 'add_payment_info', {
    send_to: cfg.ga4Id ?? DEFAULT_TRACKING.ga4Id,
    currency,
    value,
  });
  fbq('track', 'AddPaymentInfo', { value, currency, ...metaContents(data.itemId, data.itemName, value) });
}

export function trackPurchase(cfg: TrackingConfig, data: PurchaseData): void {
  if (cfg.disabled) return;
  const { transactionId, value, currency, itemId, itemName } = data;

  // GA4 purchase
  gtag('event', 'purchase', {
    send_to: cfg.ga4Id ?? DEFAULT_TRACKING.ga4Id,
    transaction_id: transactionId,
    value,
    currency,
    items: itemId ? [{ item_id: itemId, item_name: itemName, price: value, quantity: 1 }] : undefined,
  });

  // Google Ads conversion
  const adsId = cfg.googleAdsId ?? DEFAULT_TRACKING.googleAdsId;
  const adsLabel = cfg.googleAdsConversionLabel ?? DEFAULT_TRACKING.googleAdsConversionLabel;
  gtag('event', 'conversion', {
    send_to: `${adsId}/${adsLabel}`,
    value,
    currency,
    transaction_id: transactionId,
  });

  // Meta Purchase — eventID lets Meta dedupe against a later server-side
  // (CAPI) Purchase sent with the same id.
  fbq(
    'track',
    'Purchase',
    { value, currency, ...metaContents(itemId, itemName, value) },
    { eventID: transactionId },
  );
}

// --- Auto-detect price/currency from the Shopify host page ----------------

type ShopifyVariant = { id: number; price: number; title?: string };
type ShopifyProductJson = {
  id: number;
  title?: string;
  variants: ShopifyVariant[];
};

export type DetectedProduct = {
  value?: number;
  currency: string;
  itemId?: string;
  itemName?: string;
};

function parseProductHandle(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/\/products\/([a-z0-9-]+)/i);
  return m?.[1] ?? null;
}

export async function detectProduct(variantId?: string): Promise<DetectedProduct> {
  const currency = (typeof window !== 'undefined' && window.ShopifyAnalytics?.meta?.currency) || 'EUR';
  const handle = parseProductHandle();
  if (!handle) return { currency };

  try {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'omit' });
    if (!res.ok) return { currency };
    const data = (await res.json()) as ShopifyProductJson;
    const variant = (variantId && data.variants.find((v) => String(v.id) === String(variantId))) || data.variants[0];
    if (!variant) return { currency };
    return {
      value: variant.price / 100,
      currency,
      itemId: String(variant.id),
      itemName: [data.title, variant.title].filter(Boolean).join(' — '),
    };
  } catch {
    return { currency };
  }
}
